import { getRouteApi } from "@tanstack/react-router";
import type { ContactMessageIntent } from "@wags/profile-contracts/contact-session.contract.ts";
import { usePostHog } from "posthog-js/react";
import { useRef, useState } from "react";
import {
    CONTACT_FUNNEL_EVENTS,
    captureContactFunnelEvent,
} from "./contact-funnel";
import { resolveContactHandoff } from "./contact-handoff";
import {
    ContactLinkExpired,
    PRIVATE_LINK_HEADERS,
    privateLinkHead,
} from "./contact-link-shell";
import { parseProviderOutreachSearch } from "./handoff-search";

const routeApi = getRouteApi("/c/$token");

export const providerOutreachHandoffRoute = {
    validateSearch: parseProviderOutreachSearch,
    loaderDeps: ({
        search,
    }: {
        search: ReturnType<typeof parseProviderOutreachSearch>;
    }) => ({
        intent: search.intent,
        question: search.question,
    }),
    loader: ({
        params,
        deps,
    }: {
        params: { token: string };
        deps: { intent: ContactMessageIntent; question?: string };
    }) =>
        resolveContactHandoff({
            data: {
                token: params.token,
                intent: deps.intent,
                question: deps.question,
            },
        }),
    head: () => privateLinkHead("Open your message to Kits | Wags"),
    headers: () => PRIVATE_LINK_HEADERS,
};

export function ContactHandoffRoute() {
    const result = routeApi.useLoaderData();
    const posthog = usePostHog();
    const [copyState, setCopyState] = useState<"idle" | "success" | "failure">(
        "idle",
    );

    const redirectedToSms = useRef(false);
    const openSmsHandoff = (node: HTMLElement | null) => {
        if (!node || !result?.handoff.smsUrl || redirectedToSms.current) return;
        redirectedToSms.current = true;
        window.location.assign(result.handoff.smsUrl);
    };

    if (!result) return <ContactLinkExpired kind="c" />;

    const handoffResult = result;
    const { handoff } = handoffResult;
    async function copyMessage() {
        try {
            await navigator.clipboard.writeText(handoff.message);
            setCopyState("success");
            captureContactFunnelEvent(
                posthog,
                CONTACT_FUNNEL_EVENTS.copyMessageResult,
                {
                    dogId: handoffResult.listingId,
                    providerType: "unknown",
                    source: "contact_bounce",
                    contact_auth_state: false,
                    intent: handoffResult.intent,
                },
                {
                    outcome: "success",
                    handoff_path: "qr_fallback",
                },
            );
        } catch {
            setCopyState("failure");
            captureContactFunnelEvent(
                posthog,
                CONTACT_FUNNEL_EVENTS.copyMessageResult,
                {
                    dogId: handoffResult.listingId,
                    providerType: "unknown",
                    source: "contact_bounce",
                    contact_auth_state: false,
                    intent: handoffResult.intent,
                },
                {
                    outcome: "failure",
                    handoff_path: "qr_fallback",
                },
            );
        }
    }

    return (
        <main
            ref={openSmsHandoff}
            className="min-h-dvh bg-maltese-white p-6 text-brown"
        >
            <div className="mx-auto flex min-h-[80dvh] max-w-lg flex-col justify-center gap-5">
                <p className="text-body-small font-medium text-brown/50">
                    Message Kits
                </p>
                <h1 className="font-family-heading text-4xl">
                    Open Messages to send your note
                </h1>
                <a
                    className="inline-flex min-h-14 items-center justify-center rounded-nested bg-labrador-yellow px-5 text-center font-semibold text-doberman-black"
                    href={handoff.smsUrl}
                >
                    Open Messages
                </a>
                <button
                    className="inline-flex min-h-14 items-center justify-center rounded-nested border border-brown/20 px-5 text-center font-semibold text-brown"
                    onClick={() => void copyMessage()}
                    type="button"
                >
                    {copyState === "success"
                        ? "Message copied"
                        : copyState === "failure"
                          ? "Copy failed — try again"
                          : "Copy message"}
                </button>
                <p
                    data-ph-no-capture
                    className="whitespace-pre-wrap rounded-card bg-white p-card text-brown/80 shadow-sm"
                >
                    {handoff.message}
                </p>
                <p data-ph-no-capture className="text-brown/70">
                    Send it to {handoff.kitsPhoneNumber}.
                </p>
            </div>
        </main>
    );
}
